import { DefaultMap, definedMap, proveNever } from "@glideapps/ts-necessities";
import { ComponentIndexes } from "common/component-indexes";
import { SettableForwardingValueProducer } from "common/components/component-helpers";
import { ConfirmModalStyle, showConfirmModal } from "common/components/confirm-modal";
import { Data } from "common/components/data";
import { LocalDataStore } from "common/components/datastore/local-datastore";
import { RealEmailAddressValueProducer, VerifiedEmailAddressValueProducer } from "common/components/functions-glide";
import { formatLocalizedString, getLocalizedString } from "common/components/localization";
import { AppAuthentication, AppFeatures, IconImage, ScreenFlag, SourceMetadata } from "common/components/SerializedApp";
import { TableIndex } from "common/components/table-index";
import {
    AppEnvironment,
    AppEnvironmentDelegate,
    AppFacilities,
    AppOther,
    AppUserAuthenticator,
    ColumnFormatChangedCallback,
    ComponentHighlight,
    ComponentMoveAction,
    Context,
    DataStore,
    EnvironmentBoundEvaluable,
    Evaluable,
    OfflineQueue,
    RenderEnvironment,
    RootEvalEnvironment,
    UploadFileResponse,
    UploadProgressHandler,
    UploadSession,
    UserProfileTableInfo,
    ValueProducer,
    isUploadFileResponseError,
} from "common/components/types";
import { FileSizeLimitExceededError, uploadFileIntoGlideStorage } from "common/components/upload-handlers";
import { Database, PaymentInformationForBuyButtons, freeGeocodeQuota } from "common/Database";
import { TableName } from "common/database-strings";
import { Eminence, EminenceFlags, eminenceFlags, getBaseRowQuota } from "common/Database/eminence";
import {
    AllQuotaValuesWithLimitsForKinds,
    QuotaContext,
    QuotaKind,
    QuotaValues,
    QuotaValuesForKinds,
    QuotaValuesWithLimit,
    makeQuotaDocumentID,
    quotaInfos,
    quotasCollectionName,
    quotasFromDocument,
} from "common/Database/quotas";
import { logError } from "common/debug-print";
import { ReportGeocodesInAppBody, UploadComponentKind } from "common/firebase-function-types";
import { modifyColumnsInSchema } from "common/generator/computed-columns";
import { Formula, GlobalVariableNames, TableColumn, TypeSchema } from "common/generator/description";
import { LocationSettings } from "common/location-common";
import { getPersistentDeviceID } from "common/persistent-device-id";
import { DeviceFormFactor } from "common/render/form-factor";
import { Sound } from "common/sound";
import { defined, definedMapWithDefault, isOlderThan } from "common/Support";
import { YesCodeValue } from "common/yes-code-types";
import { getIsOnline } from "hooks/use-network-status";
import entries from "lodash/entries";
import fromPairs from "lodash/fromPairs";
import numeral from "numeral";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";
import { clearAppPasswordLocalStorage } from "webapp/chrome/smart/app-container/app-container-lib";
import { standalone } from "webapp/lib/device";
import {
    getAllowLoginSaveKey,
    getQuotasKey,
    localStorageGetItem,
    localStorageRemoveItem,
    localStorageSetItem,
} from "webapp/lib/local-storage";
import { flagExplicitSignOut } from "webapp/lib/session-storage";

import * as routes from "../routes";
import { isDocumentHidden, onVisibilityChange, removeOnVisibilityChangeHandler } from "./browser-hacks";

const offlineHeartbeatPeriod = 60 * 1000;
const onlineHeartbeatPeriod = 15 * 60 * 1000;
const heartbeatCheckPeriod = 10 * 1000;

const getHeartbeatPeriod = () => (getIsOnline() ? onlineHeartbeatPeriod : offlineHeartbeatPeriod);

class HeartbeatManager {
    private intervalHandle: unknown = undefined;
    private lastTime = new Date(Date.now() - getHeartbeatPeriod());
    private refcount = 0;
    private currentlyForEditor: boolean = false;
    private sendingForEditor: boolean = false;

    constructor(private readonly appID: string, private readonly appFacilities: AppFacilities) {}

    public enter(): void {
        if (this.refcount === 0) {
            onVisibilityChange(this.handleVisibilityChange);
        }
        this.refcount++;
        if (this.intervalHandle === undefined) {
            this.resetInterval();
        }
    }

    public exit(): void {
        if (this.refcount > 0) {
            this.refcount--;
        }
        if (this.refcount === 0) {
            removeOnVisibilityChangeHandler(this.handleVisibilityChange);
        }
    }

    private resetInterval(): void {
        this.lastTime = new Date(Date.now() - getHeartbeatPeriod());
        if (this.intervalHandle !== undefined) {
            clearTimeout(this.intervalHandle as any);
        }
        this.intervalHandle = setInterval(() => this.handleHeartbeat(), heartbeatCheckPeriod);
        // We want to fire the heartbeat immediately if we've requested a reset.
        this.handleHeartbeat();
    }

    private shouldHandleHeartbeat(): boolean {
        return !isDocumentHidden() && isOlderThan(this.lastTime, getHeartbeatPeriod());
    }

    private handleHeartbeat(): void {
        if (!this.shouldHandleHeartbeat()) {
            return;
        }
        if (this.refcount < 1) {
            clearTimeout(this.intervalHandle as any);
            this.intervalHandle = undefined;
        }
        this.lastTime = new Date();
        // The significance of the `editor` boolean is that only tables used in
        // the app will be reloaded on automatic refreshes if this is not set to `true`.
        // So, unless you open the Data Editor, periodic refreshes and drive notifications
        // will only cause updates to tables actually used in the app.
        //
        // This does have the side effect of not showing new tables until either
        // 1. The Data Editor has been open at least once
        // 2. The "reload" button has been pressed
        //
        // We'd do this with beaconCloudFunctionWeb, except between 2017 and 2020
        // Chrome wouldn't sendBeacon with JSON data to work around its own internal
        // security SNAFU. ensureDataLiveliness expects actual JSON, and will continue
        // to do so, so no beaconing here.
        this.appFacilities
            .callAuthIfAvailableCloudFunction(
                "ensureDataLiveliness",
                {
                    appID: this.appID,
                    editor: this.sendingForEditor,
                    deviceID: getPersistentDeviceID(this.appID),
                    standalone,
                    builder: !routes.isPlayer(),
                },
                {}
            )
            .catch(e => logError(`Could not ensureDataLiveliness: ${e}`));
        if (!this.currentlyForEditor) {
            this.sendingForEditor = false;
        }
    }

    private handleVisibilityChange = () => {
        if (this.shouldHandleHeartbeat()) {
            this.resetInterval();
        }
    };

    public enterEditor(): void {
        if (!this.sendingForEditor) {
            this.resetInterval();
        }
        this.sendingForEditor = true;
        this.currentlyForEditor = true;
        this.enter();
    }

    public exitEditor(): void {
        this.currentlyForEditor = false;
        this.exit();
    }
}

const reportQuotaIncreaseDebouncePeriod = 2000;

function getConfirmModalStringsForFailedUpload(component: UploadComponentKind): { title: string; description: string } {
    if (component === "signature") {
        return {
            title: getLocalizedString("cantAddNewSignatures"),
            description: getLocalizedString("thisAppMustBeUpgradedToAllowMoreSignatures"),
        };
    } else {
        return {
            title: getLocalizedString("thisActionCouldntBeTriggered"),
            description: getLocalizedString("thisAppMustBeUpgraded"),
        };
    }
}

function loadQuotasFromLocalStorage(appID: string): QuotaValuesForKinds | undefined {
    const fromStorage = localStorageGetItem(getQuotasKey(appID));
    if (fromStorage === undefined) return undefined;

    return JSON.parse(fromStorage) as QuotaValuesForKinds;
}

function saveQuotasToLocalStorage(appID: string, quotas: QuotaValuesForKinds): void {
    localStorageSetItem(getQuotasKey(appID), JSON.stringify(quotas));
}

export class ForwardingAppEnvironment implements AppEnvironment {
    private _delegate: AppEnvironmentDelegate | undefined;
    private readonly _highlightComponentFlags: Map<string, Data<AppOther, AppEnvironment>> = new Map();
    private _inFlightGeocodes: number = 0;
    private readonly _geocodeCounts: DefaultMap<string, number> = new DefaultMap(() => 0);
    private _geocodeQuotaReachedFlagValueProducer: SettableForwardingValueProducer<AppOther, AppEnvironment> =
        new SettableForwardingValueProducer();
    private readonly _geocodeQuotaReachedForKeyValueProducers: DefaultMap<
        string,
        SettableForwardingValueProducer<AppOther, AppEnvironment>
    > = new DefaultMap(() => new SettableForwardingValueProducer());
    private _localDataStore: LocalDataStore | undefined;
    private _heartbeatManager: HeartbeatManager;
    // We're assuming each callback only has one column format it's registering for.
    private readonly _columnFormatCallbacksToRegister: Map<ColumnFormatChangedCallback, [TableName, string]> =
        new Map();
    public readonly globalVariables: DefaultMap<string, ValueProducer> = new DefaultMap(n =>
        this.makeGlobalVariable(n)
    );
    public readonly globalValues: Map<string, ValueProducer> = new Map();
    public readonly tableIndexes: Map<string, TableIndex<AppOther, AppEnvironment>> = new Map();
    public readonly fallbackUsername: ValueProducer = new Data<AppOther, AppEnvironment>(undefined);

    private _mountCount: number = 0;
    private _quotaUnlisten: (() => void) | undefined;
    private _quotas: QuotaValuesForKinds = {};
    private _quotasFromLocalStorage: QuotaValuesForKinds | undefined;
    private readonly _quotaReachedValueProducers: DefaultMap<
        QuotaKind,
        SettableForwardingValueProducer<AppOther, AppEnvironment>
    > = new DefaultMap(() => new SettableForwardingValueProducer());

    constructor(
        public readonly authenticator: AppUserAuthenticator,
        private readonly _appFeatures: AppFeatures,
        public readonly database: Database,
        public readonly dataStore: DataStore,
        public readonly isBuilder: boolean,
        private readonly _defaultUserProfileTableInfo: UserProfileTableInfo | undefined,
        // FIXME: Move this into a global component variable, too
        public readonly appIcon: IconImage
    ) {
        this._heartbeatManager = new HeartbeatManager(this.appID, this.appFacilities);
    }

    public get appFacilities(): AppFacilities {
        return this.authenticator.appFacilities;
    }

    public get appID(): string {
        return this.authenticator.appID;
    }

    public get appUserID(): string | undefined {
        return this.authenticator.appUserID;
    }

    public get locationSettings(): LocationSettings | undefined {
        return this._delegate?.locationSettings;
    }

    public setDelegate(delegate: AppEnvironmentDelegate | undefined): void {
        this._delegate = delegate;
        if (delegate !== undefined) {
            delegate.updateGlobalVariables(this.globalVariables);

            for (const [callback, [tableName, columnName]] of this._columnFormatCallbacksToRegister) {
                delegate.registerColumnFormatChangedCallback(tableName, columnName, callback);
                callback();
            }
            this._columnFormatCallbacksToRegister.clear();
        }
        this.dataStore.appEnvironmentUpdated();
    }

    public get shortName(): string | undefined {
        return definedMap(this._delegate, cb => cb.shortName);
    }

    public get customDomain(): string | undefined {
        return definedMap(this._delegate, cb => cb.customDomain);
    }

    public get isPublished(): boolean {
        return definedMapWithDefault(this._delegate, false, cb => cb.isPublished);
    }

    public get userProfileTableInfo(): UserProfileTableInfo | undefined {
        if (this._delegate === undefined) return this._defaultUserProfileTableInfo;
        return this._delegate.userProfileTableInfo;
    }

    public get localDataStore(): DataStore {
        if (this._localDataStore === undefined) {
            this._localDataStore = new LocalDataStore(this.appID);
        }
        return this._localDataStore;
    }

    public get enableShareScreen(): boolean {
        return this.eminenceFlags.canDisableShare !== true || this.appFeatures.enableShareScreen === true;
    }

    public get shareScreenTitle(): string {
        return this.appFeatures.shareScreenTitle ?? "";
    }

    public pushBuilderScreen(
        name: string,
        ctx: Context,
        title: Evaluable | undefined,
        env: RenderEnvironment,
        flags: ReadonlySet<ScreenFlag> | undefined,
        itemID: string | undefined
    ): void {
        if (this._delegate === undefined) return;
        this._delegate.pushBuilderScreen(name, ctx, title, env, flags, itemID);
    }

    public pushModalBuilderScreen(
        name: string,
        ctx: Context,
        title: Evaluable | undefined,
        flags: ReadonlySet<ScreenFlag> | undefined
    ): void {
        if (this._delegate === undefined) return;
        this._delegate.pushModalBuilderScreen(name, ctx, title, flags);
    }

    public pushMenuScreen(
        menuID: string,
        primaryKey: string,
        defaultScreenName: string,
        ctx: Context,
        env: RenderEnvironment
    ): void {
        if (this._delegate === undefined) return;
        this._delegate.pushMenuScreen(menuID, primaryKey, defaultScreenName, ctx, env);
    }

    public popScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popScreen(env);
    }

    public popModalScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popModalScreen(env);
    }

    public popAnyScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popAnyScreen(env);
    }

    public popToRoot(env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.popToRoot(env);
    }

    public pushUserProfileScreen(): void {
        if (this._delegate === undefined) return;
        this._delegate.pushUserProfileScreen();
    }

    public setCurrentTab(index: number, env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.setCurrentTab(index, env);
    }

    public get currentPivot(): number {
        if (this._delegate === undefined) return 0;
        return this._delegate.currentPivot;
    }

    public setCurrentPivot(index: number, env: RenderEnvironment): void {
        if (this._delegate === undefined) return;
        this._delegate.setCurrentPivot(index, env);
    }

    public getHighlightComponentFlag(componentID: string): ValueProducer {
        let flag = this._highlightComponentFlags.get(componentID);
        if (flag === undefined) {
            flag = new Data<AppOther, AppEnvironment>(ComponentHighlight.None);
            this._highlightComponentFlags.set(componentID, flag);
        }
        return flag;
    }

    public componentMoved(sourceComponentID: string, targetComponentID: string, action: ComponentMoveAction): void {
        this._delegate?.componentMoved(sourceComponentID, targetComponentID, action);
    }

    public showToast(message: string, isSuccess: boolean): void {
        if (this._delegate === undefined) return;
        return this._delegate.showToast(message, isSuccess);
    }

    public playSound(name: Sound): void {
        return this._delegate?.playSound(name);
    }

    public copyToClipboard(str: string): void {
        return this._delegate?.copyToClipboard(str);
    }

    public signOut(): void {
        clearAppPasswordLocalStorage(this.appID);
        localStorageRemoveItem(getAllowLoginSaveKey(this.appID));
        flagExplicitSignOut(this.appID);
        document.location.reload();
    }

    public get paymentInformation(): PaymentInformationForBuyButtons {
        if (this._delegate === undefined) return {};
        return this._delegate.paymentInformation;
    }

    public get demonstratingProComponents(): boolean {
        if (this._delegate === undefined) return false;
        return this._delegate.demonstratingProComponents;
    }

    public get appFeatures(): AppFeatures {
        return definedMapWithDefault(this._delegate, this._appFeatures, cb => cb.modifyAppFeatures(this._appFeatures));
    }

    public get eminenceFlags(): EminenceFlags {
        return definedMap(this._delegate, cb => cb.eminenceFlags) ?? eminenceFlags[Eminence.Free];
    }

    public get authentication(): AppAuthentication | undefined {
        return definedMap(this._delegate, cb => cb.authentication);
    }

    public get deviceFormFactor(): DeviceFormFactor {
        return definedMapWithDefault(this._delegate, DeviceFormFactor.Phone, cb => cb.deviceFormFactor);
    }

    public getComputedColumn(
        schema: TypeSchema,
        tableName: TableName,
        columnName: string,
        formula: Formula,
        isBuilder: boolean,
        forDataEditor: boolean
    ): Evaluable | undefined {
        if (this._delegate === undefined) {
            logError("Missing delegate when calling getComputedColumn");
            return undefined;
        }
        return this._delegate.getComputedColumn?.(schema, tableName, columnName, formula, isBuilder, forDataEditor);
    }

    public getSourceMetadata(): readonly SourceMetadata[] | undefined {
        return this._delegate?.getSourceMetadata();
    }

    public modifyColumnsInSchema(
        oldSchema: TypeSchema,
        removed: ReadonlyMap<string, readonly string[]>,
        modified: ReadonlyMap<string, readonly TableColumn[]>,
        moved: ReadonlyMap<string, readonly [string, number][]>,
        setProtected: ReadonlyMap<string, readonly [string, boolean][]>,
        rowIDColumns: ReadonlyMap<string, string | undefined>
    ): { schema: TypeSchema; errors: readonly string[] } {
        return modifyColumnsInSchema(
            oldSchema,
            this.getSourceMetadata(),
            this.userProfileTableInfo,
            removed,
            modified,
            moved,
            setProtected,
            rowIDColumns,
            true
        );
    }

    public getHighestGeocodeCount(): number {
        let highestCount = 0;
        for (const count of this._geocodeCounts.values()) {
            if (count > highestCount) {
                highestCount = count;
            }
        }
        return highestCount;
    }

    private reportGeocodesAfterDebounce = async () => {
        const geocodes = this._inFlightGeocodes;
        if (geocodes === 0) return;

        const { appID } = this;
        this._inFlightGeocodes = 0;
        const body: ReportGeocodesInAppBody = { appID, geocodes };
        await callCloudFunctionWeb("reportGeocodesInApp", body, {});
    };

    public reportGeocode(): void {
        const { _inFlightGeocodes: prevInFlightGeocodes } = this;
        this._inFlightGeocodes++;
        if (prevInFlightGeocodes === 0) {
            setTimeout(this.reportGeocodesAfterDebounce, reportQuotaIncreaseDebouncePeriod);
        }
    }

    private outstandingQuotaUpdates: Partial<Record<QuotaKind, number>> = {};

    private reportingQuotaIncrease: boolean = false;
    private reportQuotaIncreaseAfterDebounce = async () => {
        if (this.reportingQuotaIncrease === true) return;

        while (Object.keys(this.outstandingQuotaUpdates).length > 0) {
            this.reportingQuotaIncrease = true;

            const quotaChanges = this.outstandingQuotaUpdates;
            this.outstandingQuotaUpdates = {};

            try {
                await this.appFacilities.callAuthIfAvailableCloudFunction(
                    "reportQuotaIncreases",
                    { appID: this.appID, quotaChanges },
                    {}
                );
            } finally {
                // FIXME: Should we merge the quota updates back into the "global"
                // instance in the event of a call failure?

                this.reportingQuotaIncrease = false;
            }
        }
    };

    public reportQuotaIncrease(kind: QuotaKind, increment: number): void {
        const prevUpdate = this.outstandingQuotaUpdates[kind] ?? 0;
        this.outstandingQuotaUpdates[kind] = prevUpdate + increment;
        setTimeout(this.reportQuotaIncreaseAfterDebounce, reportQuotaIncreaseDebouncePeriod);
    }

    private getGeocodeQuota(): number {
        if (this.appFeatures.noGeocodeLimit === true) {
            return 9999999;
        }
        return freeGeocodeQuota;
    }

    private setGeocodeQuotaReachedFlag(): void {
        if (this._delegate === undefined) return;

        const quota = this.getGeocodeQuota();

        let count = 0;
        for (const [key, n] of this._geocodeCounts.entries()) {
            count = Math.max(count, n);

            if (!this._geocodeQuotaReachedForKeyValueProducers.has(key)) continue;

            const isReachedForKey = n > quota;
            const vp = this._geocodeQuotaReachedForKeyValueProducers.get(key);
            if (vp.forwardee === undefined) {
                vp.setForwardee(new Data<AppOther, AppEnvironment>(isReachedForKey));
            } else {
                vp.set(isReachedForKey);
            }
        }
        const isQuotaReached = count > quota;
        if (this._geocodeQuotaReachedFlagValueProducer.forwardee === undefined) {
            this._geocodeQuotaReachedFlagValueProducer.setForwardee(new Data<AppOther, AppEnvironment>(isQuotaReached));
        } else {
            this._geocodeQuotaReachedFlagValueProducer.set(isQuotaReached);
        }
    }

    public isGeocodeWithinQuota(key: string): boolean {
        const c = this._geocodeCounts.get(key);
        return c < this.getGeocodeQuota();
    }

    public countGeocodeForQuota(key: string): void {
        this._geocodeCounts.update(key, c => c + 1);
        this.setGeocodeQuotaReachedFlag();
    }

    public getGeocodeQuotaReachedFlag(): ValueProducer {
        return this._geocodeQuotaReachedFlagValueProducer;
    }

    public getGeocodeQuotaReachedForKeyFlag(key: string): ValueProducer {
        return this._geocodeQuotaReachedForKeyValueProducers.get(key);
    }

    private loadStoredQuotas(): QuotaValuesForKinds | undefined {
        if (this._quotasFromLocalStorage === undefined) {
            this._quotasFromLocalStorage = loadQuotasFromLocalStorage(this.appID);
        }
        return this._quotasFromLocalStorage ?? (window as any).glideQuotaState;
    }

    public getQuotaValues(kind: QuotaKind): QuotaValuesWithLimit | undefined {
        let defaultLimit: number;
        switch (kind) {
            case QuotaKind.RowsUsed:
                defaultLimit = getBaseRowQuota(this.appFeatures, this.eminenceFlags);
                break;
            case QuotaKind.ZapsAndWebhook:
                defaultLimit = this.eminenceFlags.zapQuota;
                break;
            case QuotaKind.Reloads:
                defaultLimit = this.eminenceFlags.reloadQuota;
                break;
            case QuotaKind.Signatures:
                defaultLimit = this.eminenceFlags.signatureQuota;
                break;
            case QuotaKind.DeliverEmail:
                defaultLimit = this.eminenceFlags.deliverEmailQuota;
                break;
            case QuotaKind.FileBytesUsed:
                defaultLimit = this.eminenceFlags.fileStorageQuota;
                break;
            case QuotaKind.BarcodesScanned:
                defaultLimit = this.eminenceFlags.barcodeScanQuota;
                break;
            default:
                return proveNever(kind, "Invalid quota kind", undefined);
        }

        let values = this._quotas[kind];
        if (values === undefined) {
            const storedQuotas = this.loadStoredQuotas();
            values = storedQuotas?.[kind];
        }

        return {
            current: values?.current ?? 0,
            limit: values?.limit ?? defaultLimit,
        };
    }

    public getQuotaReachedFlag(kind: QuotaKind): ValueProducer {
        return this._quotaReachedValueProducers.get(kind);
    }

    public suppressNotificationTopic(topic: string): void {
        if (this._delegate === undefined) return;
        this._delegate.suppressNotificationTopic(topic);
    }

    public unsuppressNotificationTopic(topic: string): void {
        if (this._delegate === undefined) return;
        this._delegate.unsuppressNotificationTopic(topic);
    }

    private get allQuotas(): AllQuotaValuesWithLimitsForKinds {
        const pairs = quotaInfos.map(
            qi => [qi.kind, defined(this.getQuotaValues(qi.kind))] as [QuotaKind, QuotaValues]
        );
        return fromPairs(pairs) as AllQuotaValuesWithLimitsForKinds;
    }

    private updateQuotas(quotas: QuotaValuesForKinds): void {
        const prior = this.allQuotas;
        for (const [k, values] of entries(quotas)) {
            if (values === undefined) continue;

            const kind = k as QuotaKind;

            const old = this._quotas[kind];
            if (old === undefined) {
                this._quotas = { ...this._quotas, [kind]: values };
            } else {
                const updates: QuotaValues = { current: values.current, limit: values.limit ?? old.limit };
                this._quotas = { ...this._quotas, [kind]: updates };
            }

            const newValues = this.getQuotaValues(kind);
            if (newValues === undefined) continue;
            const reached = newValues.current >= newValues.limit;
            const vp = this._quotaReachedValueProducers.get(kind);
            if (vp.forwardee !== undefined) {
                vp.set(reached);
            } else {
                vp.setForwardee(new Data<AppOther, AppEnvironment>(reached));
            }
        }

        saveQuotasToLocalStorage(this.appID, this.allQuotas);

        if (this._delegate?.quotasDidUpdate !== undefined) {
            this._delegate?.quotasDidUpdate(this.allQuotas, prior);
        }
    }

    private updateQuotaListening(): void {
        if (this._mountCount > 0 && this._quotaUnlisten === undefined) {
            this._quotaUnlisten = this.database.listenToDocument(
                quotasCollectionName,
                makeQuotaDocumentID(QuotaContext.App, this.appID),
                data => this.updateQuotas(quotasFromDocument(this.database, data).quotas)
            );
        } else if (this._mountCount <= 0 && this._quotaUnlisten !== undefined) {
            this._quotaUnlisten();
            this._quotaUnlisten = undefined;
        }
    }

    public isMounted(inEditor: boolean): void {
        if (inEditor) {
            this._heartbeatManager.enterEditor();
        } else {
            this._heartbeatManager.enter();
        }

        this._mountCount += 1;
        this.updateQuotaListening();
    }

    public isUnmounted(inEditor: boolean): void {
        if (inEditor) {
            this._heartbeatManager.exitEditor();
        } else {
            this._heartbeatManager.exit();
        }

        this._mountCount -= 1;
        this.updateQuotaListening();
    }

    public registerColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void {
        if (this._delegate === undefined) {
            this._columnFormatCallbacksToRegister.set(callback, [tableName, columnName]);
            return;
        }
        this._delegate.registerColumnFormatChangedCallback(tableName, columnName, callback);
    }

    public unregisterColumnFormatChangedCallback(
        tableName: TableName,
        columnName: string,
        callback: ColumnFormatChangedCallback
    ): void {
        if (this._delegate === undefined) {
            this._columnFormatCallbacksToRegister.delete(callback);
            return;
        }
        this._delegate.unregisterColumnFormatChangedCallback(tableName, columnName, callback);
    }

    public getColumnFormat(tableName: TableName, columnName: string): EnvironmentBoundEvaluable | undefined {
        if (this._delegate === undefined) return undefined;
        return this._delegate.getColumnFormat(tableName, columnName);
    }

    public pushFeedbackScreen(env: RenderEnvironment): void {
        if (this._delegate === undefined) return undefined;
        return this._delegate.pushFeedbackScreen(env);
    }

    public uploadAppFile(
        file: File,
        component: UploadComponentKind,
        onProgress: UploadProgressHandler | undefined
    ): UploadSession {
        const { appID, appFacilities } = this;
        appFacilities.trackEvent("upload_file", { app_id: appID, content_type: file.type, content_length: file.size });

        const onAttemptResponse = async (resp: UploadFileResponse) => {
            if (!isUploadFileResponseError(resp)) {
                appFacilities.trackEvent("upload success", { app_id: appID, label: component });
                return;
            }
            let pushFeedback = false;

            if (resp.quotaExceeded) {
                appFacilities.trackEvent("upload over quota", { app_id: appID, label: component });
                pushFeedback = !(
                    (await showConfirmModal({
                        ...getConfirmModalStringsForFailedUpload(component),
                        accept: getLocalizedString("ok"),
                        cancel: getLocalizedString("support"),
                        style: ConfirmModalStyle.QuotaWarning,
                    })) ?? true
                );
            } else if (resp.error instanceof FileSizeLimitExceededError) {
                const { givenSize, maxSize } = resp.error;
                pushFeedback = !(
                    (await showConfirmModal({
                        title: getLocalizedString("fileTooLargeTitle"),
                        description: formatLocalizedString(
                            "fileTooLargeDescription",
                            [givenSize, maxSize].map(x => numeral(x).format("0.0b"))
                        ),
                        accept: getLocalizedString("ok"),
                        cancel: getLocalizedString("support"),
                        style: ConfirmModalStyle.QuotaWarning,
                    })) ?? true
                );
            }

            if (pushFeedback && this._delegate !== undefined) {
                const renv = this.makeRenderEnvironment();
                if (renv !== undefined) {
                    this.pushFeedbackScreen(renv);
                }
                return;
            }

            appFacilities.trackEvent("upload failed", { app_id: appID, label: component });
        };
        return uploadFileIntoGlideStorage(appFacilities, appID, file, component, onProgress, onAttemptResponse);
    }

    // ##makeGlobalVariable:
    // FIXME: This is duplicated in the dummy environment, and probably
    // shouldn't be in this class.
    private makeGlobalVariable(variableName: string): ValueProducer {
        if (variableName === GlobalVariableNames.VerifiedEmailAddress) {
            return new VerifiedEmailAddressValueProducer(this);
        }
        if (variableName === GlobalVariableNames.RealEmailAddress) {
            return new RealEmailAddressValueProducer(this);
        }

        const vp = this._delegate?.makeGlobalVariable(variableName);
        if (vp !== undefined) {
            return vp;
        }

        // Global variables have to be loading until they're set, not
        // `undefined`.  One case where this is particularly relevant is the
        // user profile table name.  If it were `undefined` at start, when we
        // try to load the user profile row, the fetch row value producer
        // would return `undefined`, making it seem as if the user profile row
        // is not present, as opposed to not loaded yet.  That in turn leads
        // to ##tabVisibility becoming screwed up.
        return new SettableForwardingValueProducer<AppOther, AppEnvironment>();
    }

    public updateGlobalVariables(): void {
        if (this._delegate === undefined) return undefined;

        this._delegate.updateGlobalVariables(this.globalVariables);
    }

    public makeRootEvalEnvironment(): RootEvalEnvironment {
        return defined(this._delegate?.makeRootEvalEnvironment?.());
    }

    public makeRenderEnvironment(): RenderEnvironment | undefined {
        return this._delegate?.makeRenderEnvironment();
    }

    public addImageComponent(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        imageURL: string
    ): void {
        this._delegate?.addImageComponent(screenName, componentIndex, imageURL);
    }

    public addComponentForColumn(
        screenName: string | undefined,
        componentIndex: ComponentIndexes | undefined,
        columnName: string
    ): void {
        this._delegate?.addComponentForColumn(screenName, componentIndex, columnName);
    }

    public startBuilderBusy(): void {
        this._delegate?.startBuilderBusy();
    }

    public stopBuilderBusy(): void {
        this._delegate?.stopBuilderBusy();
    }

    public makeOfflineQueue<T>(
        name: string,
        onOnline: () => void,
        idForItem: (item: T) => string
    ): OfflineQueue<T> | undefined {
        return this._delegate?.makeOfflineQueue?.(name, onOnline, idForItem);
    }

    public async callYesCode(url: string, params: ReadonlyMap<string, unknown>): Promise<YesCodeValue | undefined> {
        return await this._delegate?.callYesCode?.(url, params);
    }
}
